import { Button } from '@/components/ui/button';
import GlassCard from '@/components/ui/glass-card';
import { useSectionRecording } from '@/hooks/use-section-recording';
import { SectionType } from '@/store/slices/sectionRecordingSlice';
import { type SimplifiedAppointment } from '@/types/appointment';
import {
  ArrowLeft,
  CheckCircle2,
  Mic,
  Pause,
  Play,
  Square,
} from 'lucide-react';
import { useEffect } from 'react';

/**
 * Formats an ISO date string to MM/dd/yyyy format using UTC methods to preserve the exact date
 */
const formatDateOfService = (dateString: string): string => {
  try {
    if (!dateString) return '';

    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string provided:', dateString);
      return dateString; // Return original if invalid
    }

    // Use UTC methods to preserve the exact date as received from API
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const year = date.getUTCFullYear();

    return `${month}/${day}/${year}`;
  } catch (error) {
    console.error('Error formatting date of service:', error);
    return dateString; // Return original string if parsing fails
  }
};

/**
 * Formats date string to MM/dd/yyyy for DOB display
 */
const formatDateOfBirth = (dateString: string): string => {
  try {
    if (!dateString) return '';

    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString;
    }

    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const year = date.getUTCFullYear();

    return `${month}/${day}/${year}`;
  } catch (error) {
    console.error('Error formatting date of birth:', error);
    return dateString;
  }
};

/**
 * Formats an ISO date string to hh:mm a format (12-hour format with AM/PM)
 */
const formatAppointmentTime = (dateString: string): string => {
  try {
    if (!dateString) return '';

    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date string provided:', dateString);
      return dateString; // Return original if invalid
    }

    // Use local time for appointment time display
    const hours = date.getHours();
    const minutes = date.getMinutes();

    // Convert to 12-hour format
    const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedMinutes = String(minutes).padStart(2, '0');

    return `${hour12}:${formattedMinutes} ${ampm}`;
  } catch (error) {
    console.error('Error formatting appointment time:', error);
    return dateString; // Return original string if parsing fails
  }
};

/**
 * Formats appointment date and time combined
 */
const formatAppointmentDateTime = (dateString: string): string => {
  try {
    if (!dateString) return '';

    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString;
    }

    // Format date part
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const year = date.getUTCFullYear();

    // Format time part
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedMinutes = String(minutes).padStart(2, '0');

    return `${month}/${day}/${year} ${hour12}:${formattedMinutes} ${ampm}`;
  } catch (error) {
    console.error('Error formatting appointment date time:', error);
    return dateString;
  }
};

interface PatientInfoSidebarProps {
  selectedAppointment: SimplifiedAppointment | null;
  onBack: () => void;
  className?: string;
}

const PatientInfoSidebar = ({
  selectedAppointment,
  onBack,
  className = '',
}: PatientInfoSidebarProps) => {
  // Use the section recording hook
  const {
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    getSectionStatus,
    getSectionError,
    isAnyRecording,
    getActiveSection,
    isSectionCompleted,
    hasContext,
  } = useSectionRecording();

  if (!selectedAppointment) {
    return null;
  }

  const patientName = `${selectedAppointment.patientFirstName || ''} ${
    selectedAppointment.patientLastName || ''
  }`.trim();
  const service = selectedAppointment.service || 'N/A';
  const appointmentDateTime = formatAppointmentDateTime(
    selectedAppointment.startTime || ''
  );
  const dateOfBirth = formatDateOfBirth(
    selectedAppointment.patientBirthDate || ''
  );
  const gender = selectedAppointment.patientGender || 'N/A';

  // Check if context is available for recording
  useEffect(() => {
    if (!hasContext && selectedAppointment) {
      console.warn('No scribe job context available for section recording');
    }
  }, [hasContext, selectedAppointment]);

  // Get active section and recording status
  const anyRecording = isAnyRecording();
  const activeSection = getActiveSection();

  const handleSectionStart = async (section: SectionType) => {
    console.log('Section Recording Debug:', {
      section,
      anyRecording,
      hasContext,
      scribeJobId: selectedAppointment?.scribeJobId,
      appointmentId: selectedAppointment?.appointmentId,
    });

    // Only start if no other section is recording
    if (!anyRecording) {
      try {
        await startRecording(section);
        console.log(`✅ Started recording: ${section}`);
      } catch (error) {
        console.error(`❌ Failed to start recording ${section}:`, error);
      }
    } else {
      console.warn(
        '⚠️ Cannot start recording: Another section is already recording'
      );
    }
  };

  const handleSectionPause = async () => {
    try {
      await pauseRecording();
      console.log('Paused recording');
    } catch (error) {
      console.error('Failed to pause recording:', error);
    }
  };

  const handleSectionResume = async () => {
    try {
      await resumeRecording();
      console.log('Resumed recording');
    } catch (error) {
      console.error('Failed to resume recording:', error);
    }
  };

  const handleSectionStop = async () => {
    try {
      await stopRecording();
      console.log('Stopped recording');
    } catch (error) {
      console.error('Failed to stop recording:', error);
    }
  };

  const sections: { id: SectionType; label: string; color: string }[] = [
    { id: 'ccHpi', label: 'CC & HPI', color: 'from-gray-500 to-gray-600' },
    {
      id: 'refractions',
      label: 'Refraction',
      color: 'from-purple-500 to-purple-600',
    },
    { id: 'external', label: 'External', color: 'from-green-500 to-green-600' },
    { id: 'internal', label: 'Internal', color: 'from-blue-500 to-blue-600' },
    { id: 'other', label: 'Other', color: 'from-red-500 to-red-600' },
    {
      id: 'assessmentAndPlan',
      label: 'Assessment & Plan',
      color: 'from-yellow-500 to-yellow-600',
    },
  ];

  return (
    <div
      className={`h-full flex flex-col ${className}`}
      style={{ height: 'calc(100vh - 9.7rem)' }}>
      <GlassCard className="h-full" variant="elevated">
        <div
          style={{ height: 'calc(100vh - 11.7rem)' }}
          className="h-full flex flex-col p-4">
          {/* Back Button */}
          <div className="mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="-ml-2 text-slate-600 dark:text-slate-300 hover:text-purple-600 dark:hover:text-purple-400">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>

          {/* Patient Name Header */}
          <div className="mb-2">
            <div className="relative">
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 truncate bg-gradient-to-r from-purple-600 to-blue-600 dark:from-purple-400 dark:to-blue-400 bg-clip-text text-transparent">
                {patientName || 'Unknown Patient'}
              </h2>
              <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
                <span>{gender}</span>
                <span>•</span>
                <span>DOB: {dateOfBirth || 'N/A'}</span>
              </div>
              <div className="mt-2 mb-2 w-full h-0.5 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full" />
            </div>
          </div>

          {/* Appointment Date & Time */}
          <div className="flex items-center justify-between text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">
            <span className="font-medium">Appointment</span>
            <span className="text-right">{appointmentDateTime}</span>
          </div>

          {/* Service / Complaint */}
          <div className="flex items-center justify-between text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-6">
            <span className="font-medium">Service / Complaint</span>
            <span className="text-right">{service}</span>
          </div>

          <div className="w-full h-px bg-gradient-to-r from-transparent via-slate-300 dark:via-slate-600 to-transparent mb-6" />

          {/* Section Controls */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">
              Exam Sections
            </h3>
            <div className="space-y-2">
              {sections.map((section) => {
                const status = getSectionStatus(section.id);
                const isThisActive = activeSection === section.id;
                const isDisabled = anyRecording && !isThisActive;
                const isCompleted = isSectionCompleted(section.id);

                // Define shadow colors for each section
                const shadowColors: Record<SectionType, string> = {
                  refractions: 'shadow-purple-500/20',
                  internal: 'shadow-blue-500/20',
                  external: 'shadow-green-500/20',
                  ccHpi: 'shadow-gray-500/20',
                  other: 'shadow-red-500/20',
                  assessmentAndPlan: 'shadow-yellow-500/20',
                };

                return (
                  <div
                    key={section.id}
                    className={`flex items-center justify-between p-2 rounded-lg bg-white/50 dark:bg-slate-800/50 border border-slate-200/60 dark:border-slate-700/60 transition-all ${
                      isDisabled ? 'opacity-50' : 'opacity-100'
                    } ${
                      isThisActive
                        ? `shadow-lg ${shadowColors[section.id]}`
                        : ''
                    }`}>
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-1 h-6 rounded-full bg-gradient-to-b ${section.color}`}
                      />
                      <span
                        className={`text-sm font-medium ${
                          isThisActive
                            ? 'text-slate-800 dark:text-slate-100 font-semibold'
                            : 'text-slate-700 dark:text-slate-200'
                        }`}>
                        {section.label}
                      </span>
                      {isCompleted && (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {status === 'idle' && (
                        <Button
                          size="sm"
                          onClick={() => handleSectionStart(section.id)}
                          disabled={isDisabled}
                          className={`bg-gradient-to-r ${section.color} hover:opacity-90 hover:scale-105 text-white text-xs px-3 py-1 h-8 w-8 rounded-full transition-all flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-md`}
                          aria-label="Start Recording">
                          <Mic className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {status === 'recording' && (
                        <>
                          <Button
                            size="sm"
                            onClick={handleSectionPause}
                            className="bg-yellow-500 hover:bg-yellow-600 hover:scale-105 text-white text-xs px-3 py-1 h-8 w-8 rounded-full transition-all flex items-center justify-center shadow-md"
                            aria-label="Pause Recording">
                            <Pause
                              className="h-3.5 w-3.5"
                              fill="currentColor"
                            />
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleSectionStop}
                            className="bg-red-500 hover:bg-red-600 hover:scale-105 text-white text-xs px-3 py-1 h-8 w-8 rounded-full transition-all flex items-center justify-center shadow-md"
                            aria-label="Stop Recording">
                            <Square
                              className="h-3.5 w-3.5"
                              fill="currentColor"
                            />
                          </Button>
                        </>
                      )}
                      {status === 'paused' && (
                        <>
                          <Button
                            size="sm"
                            onClick={handleSectionResume}
                            className="bg-green-500 hover:bg-green-600 hover:scale-105 text-white text-xs px-3 py-1 h-8 w-8 rounded-full transition-all flex items-center justify-center shadow-md"
                            aria-label="Resume Recording">
                            <Play className="h-3.5 w-3.5" fill="currentColor" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleSectionStop}
                            className="bg-red-500 hover:bg-red-600 hover:scale-105 text-white text-xs px-3 py-1 h-8 w-8 rounded-full transition-all flex items-center justify-center shadow-md"
                            aria-label="Stop Recording">
                            <Square
                              className="h-3.5 w-3.5"
                              fill="currentColor"
                            />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Past History Section */}
          <div className="flex-1 min-h-0">
            <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">
              Past History
            </h3>
            <div className="space-y-2 overflow-y-auto max-h-full">
              {/* Placeholder cards - will be populated with actual data */}
              <div className="p-3 rounded-lg bg-white/50 dark:bg-slate-800/50 border border-slate-200/60 dark:border-slate-700/60">
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                  Previous Encounter
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  No history available
                </p>
              </div>
            </div>
          </div>
        </div>
      </GlassCard>
    </div>
  );
};

export default PatientInfoSidebar;
